"use client"

import type React from "react"

import { useState, useRef, useContext } from "react"
import { saveAs } from "file-saver"
import { Upload, FileSpreadsheet, Loader2, AlertCircle } from "lucide-react"
import axios from "axios"
import { AuthContext } from "../../../Auth/context/AuthContext"

interface UploadStepProps {
  onSuccess: (file: File, headers: string[], rows: string[][]) => void
  onError: (error: string) => void
}

export default function UploadStep({ onSuccess, onError }: UploadStepProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [missingInfo, setMissingInfo] = useState<{
    required: string[]
    missing: string[]
    msg: string
  } | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { auth } = useContext(AuthContext)

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    const droppedFile = e.dataTransfer.files[0]
    if (validateFile(droppedFile)) {
      setMissingInfo(null)
      setFile(droppedFile)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (selectedFile && validateFile(selectedFile)) {
      setMissingInfo(null)
      setFile(selectedFile)
    }
  }

  const validateFile = (file: File): boolean => {
    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "application/vnd.ms-excel", // .xls
      "text/csv", // .csv
    ]

    if (!validTypes.includes(file.type)) {
      onError("Please upload an Excel (.xlsx, .xls) or CSV file.")
      return false
    }

    if (file.size > 10 * 1024 * 1024) {
      // 10MB limit
      onError("File size exceeds 10MB limit.")
      return false
    }

    return true
  }

  const handleAnalyzeFile = async () => {
    if (!file || !auth?.token) return

    setLoading(true)
    onError("")
    try {
      const form = new FormData()
      form.append("excelFile", file)
      const resp = await axios.post(
        "/api/myteconstruction/contact-import/analyze",
        form,
        { headers: { Authorization: `Bearer ${auth.token}` } }
      )
      onSuccess(file, resp.data.sampleHeaders, resp.data.sampleRows)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      console.error("Analyze file error:", err)
      const resp = err?.response?.data || {}
      if (resp.code === 'MISSING_HEADERS') {
        setMissingInfo({
          required: resp.requiredHeaders || [],
          missing: resp.missingHeaders || [],
          msg: resp.message || 'Missing required headers.'
        })
      } else {
        onError("Failed to analyze file. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }
  
  // Download blank template for users when headers missing
  const downloadTemplate = async () => {
    try {
    const response = await axios.get(
      "/api/myteconstruction/contact-import/template",
      {
        headers: { Authorization: `Bearer ${auth.token}` },
        responseType: 'blob'
      }
    )
      saveAs(response.data, 'contacts_template.xlsx')
    } catch (e) {
      console.error('Template download error:', e)
      onError("Couldn't download the template. Please try again.")
    }
  }

  return (
    <div>
      <h3 className="text-lg font-medium text-white mb-4">Upload Excel File</h3>

      {/* File Drop Area */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center ${
          isDragging ? "border-blue-500 bg-blue-900/20" : "border-gray-700"
        } transition-colors duration-200`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {file ? (
          <div className="flex flex-col items-center">
            <FileSpreadsheet className="h-12 w-12 text-green-500 mb-2" />
            <p className="text-lg font-medium text-white mb-1">{file.name}</p>
            <p className="text-sm text-gray-400 mb-4">
              {(file.size / 1024).toFixed(1)} KB • {file.type}
            </p>
            <button
              onClick={() => setFile(null)}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-md text-sm"
            >
              Change File
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <Upload className="h-12 w-12 text-gray-500 mb-2" />
            <p className="text-lg font-medium text-white mb-1">Drag & Drop Your File</p>
            <p className="text-sm text-gray-400 mb-4">or click to browse (.xlsx, .xls, .csv)</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-sm"
            >
              Browse Files
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".xlsx,.xls,.csv"
              className="hidden"
            />
          </div>
        )}
      </div>

      {/* Missing headers information and template download */}
      {missingInfo && (
        <div className="bg-yellow-900/40 border border-yellow-700 rounded-lg p-4 mt-4">
          <h4 className="text-yellow-300 font-semibold mb-2 flex items-center gap-1.5">
            <AlertCircle className="h-5 w-5 text-yellow-400" /> {missingInfo.msg}
          </h4>
          <div className="grid sm:grid-cols-2 gap-4 text-sm text-gray-200">
            <div>
              <p className="font-medium mb-1">Required headers</p>
              <ul className="list-disc list-inside space-y-0.5">
                {missingInfo.required.map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-medium mb-1">Missing from your file</p>
              <ul className="list-disc list-inside space-y-0.5">
                {missingInfo.missing.map((h) => (
                  <li key={h} className="text-red-300">{h}</li>
                ))}
              </ul>
            </div>
          </div>
          <button
            onClick={downloadTemplate}
            className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md text-sm"
          >
            <FileSpreadsheet className="h-4 w-4 text-white" />
            Download blank template
          </button>
        </div>
      )}

      {/* Next Button */}
      <div className="flex justify-end mt-6">
        <button
          onClick={handleAnalyzeFile}
          disabled={!file || loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Analyzing...</span>
            </>
          ) : (
            <span>Analyze File</span>
          )}
        </button>
      </div>
    </div>
  )
}
